import type { OnInit, AfterViewInit, ElementRef } from '@angular/core';
import { Component, ChangeDetectionStrategy, Input, EventEmitter, Output, ViewChild } from '@angular/core';
import type { UntypedFormControl } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIf } from '@angular/common';

import { SpInputAbstract, MakeProvider } from '@components/sp-form-controls/abstract/sp-input-abstract';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'sp-input',
    templateUrl: './sp-input.component.html',
    styleUrls: ['./sp-input.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(SpInputComponent)],
    imports: [ReactiveFormsModule, NgIf],
})
export class SpInputComponent extends SpInputAbstract<string> implements OnInit, AfterViewInit {
    @ViewChild('inputEl') public inputEl!: ElementRef;

    @Input() public maxLength = 500;
    @Input() public minLength!: number;
    @Input() public suffix!: string;
    @Input() public type = 'input';
    @Input() public label!: string;
    @Input() public autofocus = false;
    @Input() public hint!: string;
    @Input() public control!: UntypedFormControl;

    @Output() public checkedEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() public blurEvent: EventEmitter<string> = new EventEmitter<string>();

    protected getValidValue(value: string): string {
        return value;
    }

    public showMaxInputMessageError = false;
    public maxLengthLimit!: number;

    public ngAfterViewInit(): void {
        if (this.autofocus) {
            setTimeout(() => this.focusInput());
        }
    }

    public ngOnInit(): void {
        if (this.maxLength) {
            this.validatorsConfig.push(Validators.maxLength(this.maxLength));
        }

        if (this.minLength) {
            this.validatorsConfig.push(Validators.minLength(this.minLength));
        }

        super.init();
        super.subscribeOnFormControlValueChange();
        this.subscribeForShowingErrorMessages();
    }

    public blurEventHandler(): void {
        if (this.control.errors) {
            return;
        }

        this.blurEvent.emit(this.control.value);
    }

    public focusInput(): void {
        if (this.inputEl) {
            const input = this.inputEl.nativeElement as HTMLInputElement;
            input.focus();
        }
    }

    private showErrorMessages(inputValueLength: number, maxLength: number | undefined): void {
        if (maxLength && inputValueLength === maxLength) {
            this.showMaxInputMessageError = true;
            this.maxLengthLimit = maxLength;
        }

        if (this.control.errors && this.control.errors.maxlength) {
            this.maxLengthLimit = this.control.errors.maxlength.requiredLength;
        }

        this.showMaxInputMessageError = false;
    }

    private subscribeForShowingErrorMessages(): void {
        this.control.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            const inputValueLength: number = value ? value.length : 0;
            this.showErrorMessages(inputValueLength, this.maxLength);
        });
    }
}
